<template>
    <PrimeToast />
    <Modal />
    <MQ group id="mc">
        <template #md-xxl>
            <nav class="nav">
                <Button :disabled="!getAllowedNavigation()" class="nav-btn responsive-button p-0 ml-2" link>
                    <router-link class="nav-link w-100" :to="`/`">
                        <i class="pi pi-home"></i>
                        {{ $t('Home') }}
                    </router-link>
                </Button>
                <Button v-if="!isDemoSetupStep" :disabled="!getAllowedNavigation()" class="nav-btn responsive-button p-0" link>
                    <router-link class="nav-link w-100" :to="mapURL()">
                        <i class="pi pi-map"></i>
                        {{ $t('Map') }}
                    </router-link>
                </Button>
                <Button v-if="userRole.hasAnyAccess('field_geo', 'C')" :disabled="!getAllowedNavigation()" class="nav-btn responsive-button p-0" link>
                    <router-link class="nav-link w-100" :to="mapDrawing()">
                        <i class="pi pi-pencil"></i>
                        {{ $t('Field planning') }}
                    </router-link>
                </Button>
                <Button v-if="userRole.hasAnyNexatAdminAccess()" :disabled="!getAllowedNavigation()" class="nav-btn responsive-button p-0" link>
                    <router-link class="nav-link w-100" :to="fieldConnection()">
                        <i class="pi pi-share-alt"></i>
                        {{ $t('Zone Planning') }}
                    </router-link>
                </Button>
                <DemoLocked v-if="!isDemoSetupStep" feature="fields-table" v-slot="{ locked }">
                    <Button
                        :disabled="locked || !getAllowedNavigation()"
                        class="nav-btn responsive-button p-0"
                        link
                    >
                        <router-link v-if="!locked" class="nav-link w-100" :to="fieldsManagement()">
                            <i class="pi pi-table"></i>
                            {{ $t('Fields table') }}
                        </router-link>
                        <span v-else class="nav-link w-100">
                            <i class="pi pi-table"></i>
                            {{ $t('Fields table') }}
                        </span>
                    </Button>
                </DemoLocked>
                <DemoLocked
                    v-if="userRole.hasAnyAccess('users', 'R')"
                    feature="users-table"
                    v-slot="{ locked }"
                >
                    <Button
                        :disabled="locked || !getAllowedNavigation()"
                        class="nav-btn responsive-button p-0"
                        link
                    >
                        <router-link v-if="!locked" class="nav-link w-100" :to="usersManagement()">
                            <i class="pi pi-users"></i>
                            {{ $t('Users table') }}
                        </router-link>
                        <span v-else class="nav-link w-100">
                            <i class="pi pi-users"></i>
                            {{ $t('Users table') }}
                        </span>
                    </Button>
                </DemoLocked>
                <DemoLocked
                    v-if="userRole.hasAnyAccess('machines', 'R')"
                    feature="vehicles"
                    v-slot="{ locked }"
                >
                    <Button
                        :disabled="locked || !getAllowedNavigation()"
                        class="nav-btn responsive-button p-0"
                        link
                        @click="!locked && toggleVehiclesMenu($event)"
                    >
                        <span class="nav-link w-100">
                            <i class="pi pi-car"></i>
                            {{ $t('Vehicles') }}
                        </span>
                    </Button>
                </DemoLocked>
                <Menu ref="vehiclesMenu" :model="vehiclesMenuItems" :popup="true" />
                <DemoLocked
                    v-if="userRole.hasAnyAccess('trimble', 'R')"
                    feature="trimble"
                    v-slot="{ locked }"
                >
                    <Button
                        :disabled="locked || !getAllowedNavigation()"
                        class="nav-btn responsive-button p-0"
                        link
                    >
                        <router-link v-if="!locked" class="nav-link w-100" :to="trimbleManagement()">
                            <i class="fa-solid fa-globe"></i>
                            {{ $t('Trimble') }}
                        </router-link>
                        <span v-else class="nav-link w-100">
                            <i class="fa-solid fa-globe"></i>
                            {{ $t('Trimble') }}
                        </span>
                    </Button>
                </DemoLocked>
                <AccountButton />
            </nav>
            <div v-if="!getLoading()" class="mc">
                <router-view> </router-view>
            </div>
            <div v-else>
                <div class="d-flex justify-content-center align-items-center p-2" style="height: 100vh;">
                    <ProgressSpinner style="width: 50px; height: 50px" strokeWidth="8" fill="var(--surface-ground)"
                        animationDuration=".5s" aria-label="Custom ProgressSpinner" />
                </div>
            </div>
        </template>
        <template #xs-sm>
            <nav class="nav">
                <Button
                    icon="pi pi-bars"
                    class="nav-btn mobile-menu-btn ml-2"
                    :disabled="!getAllowedNavigation()"
                    variant="text"
                    @click="toggleMobileNav"
                    aria-haspopup="true"
                    aria-controls="mobile_nav_menu"
                    aria-label="Menu"
                />
                <Menu
                    ref="mobileNavMenu"
                    id="mobile_nav_menu"
                    :model="mobileNavItems"
                    :popup="true"
                >
                    <template #item="{ item, props }">
                        <DemoLocked
                            v-if="item.demoFeature"
                            :feature="item.demoFeature"
                            :show-badge="false"
                            v-slot="{ locked }"
                            class="w-100"
                        >
                            <a
                                class="mobile-nav-item flex align-items-center gap-2 w-100"
                                :class="{ 'mobile-nav-item--locked': locked || item.disabled }"
                                href="#"
                                @click.prevent="!(locked || item.disabled) && item.command && item.command()"
                            >
                                <span :class="item.icon"></span>
                                <span class="mobile-nav-item__label">{{ item.label }}</span>
                                <i
                                    v-if="locked"
                                    class="fa-solid fa-lock mobile-nav-item__lock"
                                    aria-hidden="true"
                                ></i>
                            </a>
                        </DemoLocked>
                        <a
                            v-else
                            v-bind="props.action"
                            class="mobile-nav-item flex align-items-center gap-2 w-100"
                            :class="{ 'mobile-nav-item--locked': item.disabled }"
                        >
                            <span :class="item.icon"></span>
                            <span class="mobile-nav-item__label">{{ item.label }}</span>
                        </a>
                    </template>
                </Menu>
                <AccountButton :sm="true" />
            </nav>
            <div class="mc">
                <router-view></router-view>
            </div>
        </template>
    </MQ>
</template>

<script>
import AccountButton from './components/account/AccountButton.vue'
import { useMaps } from './stores/maps'
import { useMq } from 'vue3-mq'
import Modal from './components/modal/Modal.vue'
import { useDrawingStore } from './stores/drawing'
import { useAppState } from './stores/appState'
import { useCustomers } from './stores/customers'
import { useUserRole } from './stores/userRole'
import { useUsers } from './stores/users'
import { useTrimbleStore } from './stores/trimble'
import { useRouter } from 'vue-router'
import { useDemoStore } from './stores/demoStore'
import DemoLocked from './components/demo/DemoLocked.vue'

export default {
    name: 'App',
    components: {
        AccountButton,
        Modal,
        DemoLocked,
    },
    setup() {
        const mapStore = useMaps()
        const mediaQ = useMq()
        const drawingStore = useDrawingStore()
        const appState = useAppState()
        const customers = useCustomers()
        const userRole = useUserRole()
        const users = useUsers()
        const trimble = useTrimbleStore()
        const router = useRouter()
        const demo = useDemoStore()

        return {
            mapStore: mapStore,
            mediaQ,
            drawingStore,
            appState,
            customers,
            userRole,
            users,
            trimble,
            router,
            demo
        }
    },
    computed: {
        checksso() {
            return this.$keycloak.authenticated
        },
        isDemo() {
            return this.demo.isDemo
        },
        isDemoSetupStep() {
            return this.isDemo && this.customers.customers.length === 0
        },
        vehiclesMenuItems() {
            return [
                {
                    label: this.$t('Vehicles table'),
                    icon: 'pi pi-table',
                    command: () => this.router.push('/vehicles-management'),
                },
                {
                    label: this.$t('Vehicle tracking'),
                    icon: 'fa-solid fa-map-location-dot',
                    command: () => this.router.push(this.vehicleTracking()),
                },
            ]
        },
        mobileNavItems() {
            const navAllowed = this.getAllowedNavigation()
            const items = [
                {
                    label: this.$t('Home'),
                    icon: 'pi pi-home',
                    disabled: !navAllowed,
                    command: () => this.navigateMobile('/'),
                },
                {
                    label: this.$t('Map'),
                    icon: 'pi pi-map',
                    disabled: !navAllowed,
                    command: () => {
                        this.onMapClick()
                        this.navigateMobile(this.mapURL())
                    },
                },
            ]

            if (this.userRole.hasAnyAccess('field_geo', 'C')) {
                items.push({
                    label: this.$t('Field planning'),
                    icon: 'pi pi-pencil',
                    disabled: !navAllowed,
                    command: () => {
                        this.onDrawingClick()
                        this.navigateMobile(this.mapDrawing())
                    },
                })
            }

            if (this.userRole.hasAnyNexatAdminAccess()) {
                items.push({
                    label: this.$t('Zone Planning'),
                    icon: 'pi pi-share-alt',
                    disabled: !navAllowed,
                    command: () => this.navigateMobile(this.fieldConnection()),
                })
            }

            items.push({
                label: this.$t('Fields table'),
                icon: 'pi pi-table',
                demoFeature: 'fields-table',
                disabled: !navAllowed || this.demo.isBlocked('fields-table'),
                command: () => this.navigateMobile(this.fieldsManagement()),
            })

            if (this.userRole.hasAnyAccess('users', 'R')) {
                items.push({
                    label: this.$t('Users table'),
                    icon: 'pi pi-users',
                    demoFeature: 'users-table',
                    disabled: !navAllowed || this.demo.isBlocked('users-table'),
                    command: () => this.navigateMobile(this.usersManagement()),
                })
            }

            if (this.userRole.hasAnyAccess('machines', 'R')) {
                items.push(
                    {
                        label: this.$t('Vehicles table'),
                        icon: 'pi pi-table',
                        demoFeature: 'vehicles',
                        disabled: !navAllowed || this.demo.isBlocked('vehicles'),
                        command: () => this.navigateMobile('/vehicles-management'),
                    },
                    {
                        label: this.$t('Vehicle tracking'),
                        icon: 'fa-solid fa-map-location-dot',
                        demoFeature: 'vehicles',
                        disabled: !navAllowed || this.demo.isBlocked('vehicles'),
                        command: () => this.navigateMobile(this.vehicleTracking()),
                    },
                )
            }

            if (this.userRole.hasAnyAccess('trimble', 'R')) {
                items.push({
                    label: this.$t('Trimble'),
                    icon: 'fa-solid fa-globe',
                    demoFeature: 'trimble',
                    disabled: !navAllowed || this.demo.isBlocked('trimble'),
                    command: () => this.navigateMobile(this.trimbleManagement()),
                })
            }

            return items
        },
    },
    methods: {
        getLoading() {
            return this.userRole.loading
        },
        navigateMobile(path) {
            this.$refs.mobileNavMenu?.hide?.()
            this.router.push(path)
        },
        toggleMobileNav(event) {
            this.$refs.mobileNavMenu.toggle(event)
        },
        mapURL() {
            const customerId = this.customers.selectedCustomers[0]?.id
            return this.mapStore.buildPath('fields', customerId)
        },
        mapDrawing() {
            const customerId = this.customers.selectedCustomers[0]?.id
            return this.mapStore.buildPath('drawing', customerId)
        },
        fieldConnection() {
            const customerId = this.customers.selectedCustomers[0]?.id
            return this.mapStore.buildPath('fields-connection', customerId)
        },
        vehicleTracking() {
            const customerId = this.customers.selectedCustomers[0]?.id
            return this.mapStore.buildPath('vehicle-tracking', customerId, false)
        },
        onDrawingClick() {
            this.appState.showFieldList()
        },
        onMapClick() {
            this.appState.hideFieldList()
        },
        getAllowedNavigation() {
            return this.customers.customers.length > 0 && !this.drawingStore.getLoading()
        },
        fieldsManagement() {
            return '/fields-management'
        },
        usersManagement() {
            return '/users-management'
        },
        vehiclesManagement() {
            return '/vehicles-management'
        },
        trimbleManagement() {
            return `/trimble-management`
        },
        toggleVehiclesMenu(event) {
            this.$refs.vehiclesMenu.toggle(event)
        },
    },
}
</script>

<style lang="scss">
@import url('./assets/main.scss');

.nav-btn {
    height: 40px;
    min-width: 66px;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    width: auto;
    z-index: 1;
    &:disabled {
        pointer-events: none;
    }
}

/* Desktop - show text with icons */
@media (min-width: 768px) {
    .responsive-button .pi,
    .responsive-button [class*='fa-'] {
        display: inline-block;
        margin-right: 0.5rem;
        font-size: 0.9rem;
    }
}

.mobile-menu-btn {
    min-width: 44px;
    width: 44px;
}

.mobile-nav-item {
    padding: 0.65rem 1rem;
    color: inherit;
    text-decoration: none;
    box-sizing: border-box;
}

.mobile-nav-item__label {
    flex: 1;
    min-width: 0;
}

.mobile-nav-item__lock {
    flex-shrink: 0;
    font-size: 0.75rem;
    margin-left: 0.5rem;
    color: #f59e0b;
}

.mobile-nav-item--locked {
    color: #a8b0bc !important;
    cursor: not-allowed;
    opacity: 1;
}
.button-link {
    background: transparent;
    border: none;
    box-shadow: none;
    outline: none;
    overflow: hidden;
    text-decoration: none; /* Remove the default link underline */
    display: inline-block; /* Ensure it acts like a button */
    margin-left: 10px;
}

.button-link > button {
    background-color: #60a5fa;
    border: none;
    box-shadow: none;
    outline: none;
}

.button-link > button:focus {
    background-color: #93c5fd;
}

.button-link > button:active {
    background-color: #93c5fd;
}

#mc {
    height: 100%;
}

#mc > div {
    height: 100%;
}
</style>
